import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import { useMapsLoader } from '../../../../components/MapsProvider';
import { DEFAULT_MAP_CENTER } from '../../../../config/maps';
import { useDriverLocationsStore } from '../../../../store/driverLocationsStore';

/**
 * Driver-facing live map. Reads the device GPS via the browser's
 * Geolocation API and:
 *   1. Centers the map on the driver's current position.
 *   2. Pushes each fix to POST /api/v1/driver-locations so the admin
 *      AdminLiveTracking map and personnel list update in near-real-time.
 *
 * The push interval is throttled to once every 30s so we don't hammer the
 * backend; the map itself updates on every browser geolocation tick.
 *
 * Falls back to a friendly "Map unavailable" placeholder when the API key
 * is missing or geolocation is denied — the rest of the dashboard
 * continues to render.
 */

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
};

const mapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
};

// Backend write throttle (ms). Browser-side updates can fire much faster.
const PUSH_INTERVAL_MS = 30_000;

export const DriverLocationMap: React.FC<{ height?: number }> = ({ height = 280 }) => {
  const { isLoaded, loadError, isConfigured } = useMapsLoader();
  const recordLocation = useDriverLocationsStore((s) => s.record);

  const [position, setPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const lastPushAt = useRef<number>(0);

  // Subscribe to GPS updates while mounted; unsubscribe on unmount.
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoError('Geolocation is not supported in this browser.');
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const next = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setPosition(next);
        setAccuracy(pos.coords.accuracy);
        setGeoError(null);

        const now = Date.now();
        if (now - lastPushAt.current >= PUSH_INTERVAL_MS) {
          lastPushAt.current = now;
          recordLocation({
            latitude: next.lat,
            longitude: next.lng,
            accuracy_meters: pos.coords.accuracy,
            heading_degrees: pos.coords.heading ?? undefined,
            speed_kmh: pos.coords.speed != null ? pos.coords.speed * 3.6 : undefined,
            captured_at: new Date(pos.timestamp).toISOString(),
          }).catch(() => {
            // Silent — surfaced via store error state if needed.
          });
        }
      },
      (err) => {
        setGeoError(err.message || 'Could not read location');
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 15_000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [recordLocation]);

  const center = useMemo(() => position ?? DEFAULT_MAP_CENTER, [position]);

  const wrap: React.CSSProperties = {
    width: '100%',
    height,
    background: '#E8EDF2',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: 16,
  };

  const overlay = (msg: string) => (
    <div style={wrap}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6B7280',
          fontSize: 13,
          textAlign: 'center',
          padding: 16,
        }}
      >
        {msg}
      </div>
    </div>
  );

  if (!isConfigured) {
    return overlay('Map unavailable. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env to enable.');
  }
  if (loadError) return overlay(`Map failed to load: ${loadError.message}`);
  if (!isLoaded) return overlay('Loading map…');
  if (geoError) return overlay(geoError);

  return (
    <div style={wrap}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={position ? 14 : 11}
        options={mapOptions}
      >
        {position && (
          <MarkerF
            position={position}
            title={`You are here${accuracy ? ` (±${Math.round(accuracy)}m)` : ''}`}
          />
        )}
      </GoogleMap>
      {!position && (
        <div
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 12,
            color: '#374151',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}
        >
          Acquiring GPS…
        </div>
      )}
    </div>
  );
};
